import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Drawer } from 'antd';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { parseDateOnly } from 'services/DateService';
import { Colors } from 'context';
import { Button, ButtonSizes, Icon, IconNames } from 'atoms';
import { formatSalaryRange, useCareerVacancy } from 'core';

export interface ICareerVacancyDetails {
  /** `null` держит панель закрытой. Панель сама грузит вакансию по id —
   *  именно поэтому её больше не закрывает фоновой перезапрос ленты. */
  vacancyId: string | null;
  onClose: () => void;
  onSave: () => void;
  /**
   * Можно ли сохранять. `false` — предпросмотр методиста (ТЗ 0039 доводка).
   * 🔴 Проп нужен и ЗДЕСЬ, не только на карточке: закладку в карточке спрятали,
   * а в подробном виде она осталась — то есть кнопку просто перенесли туда,
   * где её не искали.
   */
  canSave?: boolean;
  actionLoading: boolean;
}

/**
 * Подробный вид вакансии.
 *
 * Зачем понадобился: в карточке описание обрезано тремя строками
 * (`-webkit-line-clamp`), а прочитать его целиком было НЕГДЕ — отдельной
 * страницы вакансии в LMS нет. Владелец на ручной проверке так и сказал:
 * непонятно, что это за вакансия и что с ней делать.
 *
 * Данные берёт СВОИМ запросом по id (ТЗ 0035), а не из загруженного списка.
 * Раньше он держался за `vacancies.find(...)` на запросе с политикой
 * `cache-and-network`, и фоновой перезапрос или смена фильтра закрывали дровер
 * на середине чтения. Плюс под пагинацией нужной строки в загруженной странице
 * может не оказаться вовсе, а полное описание теперь живёт только здесь —
 * список возит укороченное.
 */
export const CareerVacancyDetails = memo(
  ({ vacancyId, onClose, onSave, actionLoading, canSave = true }: ICareerVacancyDetails) => {
    const { t } = useTranslation();
    const { vacancy } = useCareerVacancy(vacancyId);

    if (!vacancyId || !vacancy) {
      return null;
    }

    // Только 'saved' — тот же инвариант, что в CareerVacancyCard: дровер и
    // карточка показывают одну вакансию и обязаны сходиться.
    const isSaved = vacancy.myInteraction === 'saved';
    const salary = formatSalaryRange(
      vacancy.salaryMin ?? null,
      vacancy.salaryMax ?? null,
      vacancy.salaryCurrency ?? '',
    );

    const facts: { key: string; icon: IconNames; label: string; value: string }[] = [
      {
        key: 'location',
        icon: IconNames.VacancyLocation,
        label: t('careerCenter.vacancies.facts.location'),
        value: vacancy.location,
      },
      {
        key: 'workMode',
        icon: IconNames.VacancyMode,
        label: t('careerCenter.vacancies.facts.workMode'),
        value: vacancy.workMode
          ? t(`careerProfile.workMode.${vacancy.workMode}`, { defaultValue: vacancy.workMode })
          : '',
      },
      {
        key: 'seniority',
        icon: IconNames.VacancyLevel,
        label: t('careerCenter.vacancies.facts.seniority'),
        value: vacancy.seniority
          ? t(`careerProfile.seniority.${vacancy.seniority}`, { defaultValue: vacancy.seniority })
          : '',
      },
      {
        key: 'employment',
        icon: IconNames.VacancyEmployment,
        label: t('careerCenter.vacancies.facts.employment'),
        value: vacancy.employmentType
          ? t(`careerCenter.vacancies.employment.${vacancy.employmentType}`, {
              defaultValue: vacancy.employmentType,
            })
          : '',
      },
      {
        key: 'salary',
        icon: IconNames.VacancySalary,
        label: t('careerCenter.vacancies.facts.salary'),
        value: salary,
      },
      {
        key: 'expires',
        icon: IconNames.Calendar,
        label: t('careerCenter.vacancies.expiresAt'),
        value: vacancy.expiresAt ? parseDateOnly(vacancy.expiresAt) : '',
      },
    ].filter((fact) => Boolean(fact.value));

    return (
      <Drawer open onClose={onClose} width={560} title={vacancy.title} destroyOnClose>
        <CompanyLine>
          {vacancy.companyName}
          {vacancy.location ? ` · ${vacancy.location}` : ''}
        </CompanyLine>

        {facts.length ? (
          <FactList>
            {facts.map((fact) => (
              <FactRow key={fact.key}>
                <FactIcon>
                  <Icon name={fact.icon} width="16px" height="16px" />
                </FactIcon>
                <FactLabel>{fact.label}</FactLabel>
                <FactValue>{fact.value}</FactValue>
              </FactRow>
            ))}
          </FactList>
        ) : null}

        {vacancy.description ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.description')}</SectionTitle>
            {/* Описание — свободный текст из ERP; переносы строк сохраняем. */}
            <DescriptionText>{vacancy.description}</DescriptionText>
          </Section>
        ) : null}

        {(vacancy.directionTitles ?? []).length ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.directions')}</SectionTitle>
            <TagRow>
              {(vacancy.directionTitles ?? []).map((title) => (
                <Tag key={title}>{title}</Tag>
              ))}
            </TagRow>
          </Section>
        ) : null}

        {/* В карточке навыки обрезаны восемью — здесь показываем все. */}
        {(vacancy.skillNames ?? []).length ? (
          <Section>
            <SectionTitle>{t('careerCenter.vacancies.facts.skills')}</SectionTitle>
            <TagRow>
              {(vacancy.skillNames ?? []).map((skill) => (
                <Tag key={skill}>{skill}</Tag>
              ))}
            </TagRow>
          </Section>
        ) : null}

        <Footer>
          {canSave ? (
            <SaveButton
              variant="outline"
              size={ButtonSizes.sm}
              disabled={actionLoading}
              onClick={onSave}>
              {t(isSaved ? 'careerCenter.vacancies.unsave' : 'careerCenter.vacancies.save')}
            </SaveButton>
          ) : null}

          <OpenLink href={vacancy.applyUrl} target="_blank" rel="noopener noreferrer">
            {t('careerCenter.vacancies.open')}
          </OpenLink>
        </Footer>

        <FooterNote>{t('careerCenter.vacancies.applyElsewhere')}</FooterNote>
      </Drawer>
    );
  },
);

const CompanyLine = styled.div`
  margin-bottom: 20px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const FactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  background-color: ${Colors.bg_gray};
`;

const FactRow = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
`;

const FactIcon = styled.span`
  display: inline-flex;
  color: ${Colors.gray_dark};
`;

const FactLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FactValue = styled.span`
  text-align: right;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Section = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 8px 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const DescriptionText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  background-color: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 28px;
`;

const SaveButton = styled(Button)`
  width: auto;
`;

const OpenLink = styled.a`
  padding: 10px 18px;
  border-radius: 10px;
  background-color: ${Colors.main};
  /* На жёлтой заливке только btn_text: white = #FFF в светлой теме даёт
     контраст 1.2:1 — главная кнопка вакансии не читалась (правило в
     context/constants/colors.ts). */
  color: ${Colors.btn_text};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    opacity: 0.9;
    color: ${Colors.btn_text};
  }
`;

/** Честная подпись: откликнуться из системы нельзя, отклик идёт у работодателя. */
const FooterNote = styled.p`
  margin: 12px 0 0 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
`;
